import { useEffect, useState } from 'react';
import { cn } from '@/lib/core';
import { useClientTranslation } from '@/lib/core/i18n';
import { LogoLoading } from '@/components/shared/loadings/logo';
import { Button } from '@/components/ui/button';

/** Number of dots and the circle they sit on, measured from Figma node 1716:94712. */
const DOT_COUNT = 8;
const RADIUS_PX = 16;

/**
 * The eight-dot ring beneath the logo on the payment-processing screen.
 *
 * The whole ring rotates while each dot keeps a fixed opacity, which reads as a trail chasing the
 * leading dot. Built from positioned spans rather than a border-based spinner (`SpinLoading`)
 * because the design's dots are discrete and individually shaded.
 */
function DotSpinner() {
  return (
    <div
      className="relative size-10 animate-spin"
      style={{ animationDuration: '1.2s' }}
      role="status"
      aria-hidden="true"
    >
      {Array.from({ length: DOT_COUNT }, (_, index) => {
        const angle = (index / DOT_COUNT) * 2 * Math.PI;
        return (
          <span
            key={index}
            className="bg-jncquoi-neutral-1000 absolute size-1.25 rounded-full"
            style={{
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) translate(${Math.sin(angle) * RADIUS_PX}px, ${-Math.cos(angle) * RADIUS_PX}px)`,
              opacity: 0.15 + (index / (DOT_COUNT - 1)) * 0.85,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * How long before the "leave this screen" escape appears.
 *
 * Long enough that a card payment — which settles in a few seconds — never shows it, short enough
 * that nobody is held on a spinner wondering whether the app has died.
 */
const ESCAPE_AFTER_MS = 20_000;

interface PaymentProcessingProps {
  /**
   * Extra line under the logo. Set while an MB WAY payment sits in `RequiresAction` so the customer
   * knows the app is waiting on them, not stuck.
   */
  subtitle?: string;
  /**
   * Lets the customer leave once the wait stops looking normal.
   *
   * Polling is only *watching* the payment — Stripe's webhook completes it server-side either way —
   * so walking away costs nothing and cannot double-charge. Without this the screen is a trap: a
   * webhook that never lands leaves no back arrow and no way out but a refresh, which resumes the
   * same wait.
   */
  onLeave?: () => void;
  leaveLabel?: string;
  /**
   * Extra classes on the root. Used to stack this above the checkout form, which stays mounted
   * underneath so Stripe's Elements survive the confirm call.
   */
  className?: string;
}

/**
 * Full-screen state while a payment is confirming and being polled (Figma: Checkout loading
 * payment, node 1716:94712).
 *
 * The logo is passed `progress={1}` so it renders solid: unlike the registration step
 * (`flows/event/registration.tsx`), which fills the logo as a progress bar, payment duration is
 * unbounded — MB WAY waits on the customer's phone — so a fill would either finish and then sit
 * there lying, or crawl. The ring carries the "still working" signal instead.
 */
export function PaymentProcessing({ subtitle, onLeave, leaveLabel, className }: PaymentProcessingProps) {
  const t = useClientTranslation();
  const [canLeave, setCanLeave] = useState(false);

  useEffect(() => {
    if (!onLeave) return;
    const timer = setTimeout(() => setCanLeave(true), ESCAPE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [onLeave]);

  return (
    <LogoLoading
      className={cn('bg-white', className)}
      progress={1}
      belowLogo={<DotSpinner />}
      caption={{ title: t('App.Checkout.Processing'), subtitle }}
      header={
        <div className="pt-safe-area flex h-13 shrink-0 items-center justify-center">
          <h1 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-normal tracking-[0.5px] uppercase">
            {t('App.Checkout.Title')}
          </h1>
        </div>
      }
      footer={
        canLeave && onLeave ? (
          <div className="flex flex-col items-center gap-3 px-4 pb-[calc(3rem+env(safe-area-inset-bottom))]">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-center text-[12px] tracking-[0.5px]">
              {t('App.Checkout.PaymentTimedOut')}
            </p>
            <Button variant="secondary-outline" palette="dark" size="md" className="w-full" onClick={onLeave}>
              {leaveLabel ?? t('App.Labels.GoBack')}
            </Button>
          </div>
        ) : undefined
      }
    />
  );
}
